import { useState, useRef } from 'react';
import { 
  Download, 
  Upload, 
  FileSpreadsheet,
  Loader2
} from 'lucide-react';
import { BackupFile, downloadBackup, downloadCsvExport, importBackup } from '../services/api';
import Card from '../components/common/Card';
import PageHeader from '../components/common/PageHeader';
import { showToast } from '../components/common/Toast';
import ConfirmDialog from '../components/common/ConfirmDialog';

export default function Settings() {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [pendingBackup, setPendingBackup] = useState<BackupFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      await downloadBackup();
      showToast('Backup downloaded successfully', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Failed to export backup', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const backup = JSON.parse(text) as BackupFile;
      
      // Basic validation
      if (!backup.metadata || !backup.data) {
        throw new Error('Invalid backup file format');
      }
      
      setPendingBackup(backup);
      setConfirmRestore(true);
    } catch (error: any) {
      showToast(error?.message || 'Failed to read backup file', 'error');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmRestore = async () => {
    if (!pendingBackup) return;
    
    setIsImporting(true);
    setConfirmRestore(false);
    
    try {
      const result = await importBackup(pendingBackup);
      showToast(
        `Backup restored successfully. Imported: ${result.imported.incomes || 0} incomes, ${result.imported.envelopes || 0} envelopes, ${result.imported.transactions || 0} transactions, ${result.imported.debts || 0} debts.`,
        'success'
      );
    } catch (error: any) {
      showToast(error?.message || 'Failed to restore backup', 'error');
    } finally {
      setIsImporting(false);
      setPendingBackup(null);
    }
  };

  const handleCsvExport = async (type: 'transactions' | 'envelopes' | 'incomes' | 'debts') => {
    try {
      await downloadCsvExport(type);
      showToast(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`, 'success');
    } catch (error: any) {
      showToast(error?.message || `Failed to export ${type}`, 'error');
    }
  };

  const getBackupSummary = () => {
    if (!pendingBackup) return '';
    const counts = pendingBackup.metadata.table_counts;
    if (counts) {
      return `${counts.incomes} income sources, ${counts.envelopes} envelopes, ${counts.transactions} transactions, ${counts.debts} debts`;
    }
    return `${pendingBackup.data.incomes.length} income sources, ${pendingBackup.data.envelopes.length} envelopes, ${pendingBackup.data.transactions.length} transactions, ${pendingBackup.data.debts.length} debts`;
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Settings" 
        subtitle="Manage your data and application settings"
      />

      {/* Data Management Section */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-text-header mb-4">Data Management</h2>
          
          {/* Full Backup Section */}
          <div className="mb-6">
            <h3 className="text-md font-medium text-text-header mb-3">Full Backup</h3>
            <p className="text-sm text-text-muted mb-4">
              Export or restore a complete backup of all your data including incomes, envelopes, transactions, and debts.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportBackup}
                disabled={isExporting}
                className="btn btn-primary inline-flex items-center"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export Full Backup
              </button>
              
              <label className="btn btn-secondary inline-flex items-center cursor-pointer">
                {isImporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Restore from Backup
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  disabled={isImporting}
                  className="sr-only"
                />
              </label>
            </div>
          </div>

          {/* Divider */}
          <hr className="my-6 border-border" />

          {/* Individual Exports Section */}
          <div>
            <h3 className="text-md font-medium text-text-header mb-3">Export to CSV</h3>
            <p className="text-sm text-text-muted mb-4">
              Export individual data types as CSV files for use in spreadsheets or other applications.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => handleCsvExport('transactions')}
                className="btn btn-secondary inline-flex items-center justify-center"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Transactions
              </button>
              
              <button
                onClick={() => handleCsvExport('envelopes')}
                className="btn btn-secondary inline-flex items-center justify-center"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Envelopes
              </button>
              
              <button
                onClick={() => handleCsvExport('incomes')}
                className="btn btn-secondary inline-flex items-center justify-center"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Incomes
              </button>
              
              <button
                onClick={() => handleCsvExport('debts')}
                className="btn btn-secondary inline-flex items-center justify-center"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Debts
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Deployment Info Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-text-header mb-4">About</h2>
          <div className="space-y-2 text-sm text-text-muted">
            <p><strong className="text-text-header">Application:</strong> Budget - Personal Finance</p>
            <p><strong className="text-text-header">Version:</strong> 1.0.0</p>
            <p><strong className="text-text-header">Storage:</strong> Local SQLite Database</p>
            <p className="text-xs text-text-muted mt-4">
              Your data is stored locally and encrypted. Regular backups are recommended.
            </p>
          </div>
        </div>
      </Card>

      {/* Confirm Restore Dialog */}
      <ConfirmDialog
        isOpen={confirmRestore}
        title="Restore Backup"
        message={pendingBackup ? `Warning: This will replace all your current data! This action cannot be undone. Backup created: ${new Date(pendingBackup.metadata.created_at).toLocaleString()}. Contains: ${getBackupSummary()}.` : ''}
        confirmText="Restore Backup"
        cancelText="Cancel"
        onConfirm={handleConfirmRestore}
        onClose={() => {
          setConfirmRestore(false);
          setPendingBackup(null);
        }}
        variant="danger"
        isLoading={isImporting}
      />
    </div>
  );
}
